const deepClone = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (error) {
    return value;
  }
};

export const getObjectsByGroupId = (objects = [], groupId = null) => {
  if (!groupId) return [];
  return objects.filter((obj) => obj.group === groupId);
};

export const getGroupBounds = (groupObjects = []) => {
  if (!groupObjects.length) {
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      right: 0,
      bottom: 0,
    };
  }

  const minX = Math.min(...groupObjects.map((obj) => obj.x));
  const minY = Math.min(...groupObjects.map((obj) => obj.y));
  const maxRight = Math.max(...groupObjects.map((obj) => obj.x + obj.width));
  const maxBottom = Math.max(...groupObjects.map((obj) => obj.y + obj.height));

  return {
    x: minX,
    y: minY,
    width: maxRight - minX,
    height: maxBottom - minY,
    right: maxRight,
    bottom: maxBottom,
  };
};

export const moveGroupObjects = (objects = [], groupId = null, deltaX = 0, deltaY = 0) => {
  if (!groupId) return objects;

  return objects.map((obj) =>
    obj.group === groupId
      ? {
          ...obj,
          x: obj.x + deltaX,
          y: obj.y + deltaY,
        }
      : obj
  );
};

export const normalizeGroupZIndexes = (objects = [], groupId = null) => {
  if (!groupId) return objects;

  const groupObjects = objects
    .filter((obj) => obj.group === groupId)
    .sort((a, b) => {
      const az = a.z_index ?? 0;
      const bz = b.z_index ?? 0;
      if (az !== bz) return az - bz;
      return a.id > b.id ? 1 : -1;
    });

  if (!groupObjects.length) return objects;

  const minGroupZ = Math.min(...groupObjects.map((obj) => obj.z_index ?? 0));
  const zMap = new Map();

  groupObjects.forEach((obj, index) => {
    zMap.set(obj.id, minGroupZ + index);
  });

  return objects.map((obj) =>
    zMap.has(obj.id)
      ? {
          ...obj,
          z_index: zMap.get(obj.id),
        }
      : obj
  );
};

export const duplicateGroupObjects = (
  objects = [],
  groupId = null,
  newGroupId = null,
  options = {}
) => {
  if (!groupId) return [];

  const {
    offsetX = 20,
    offsetY = 20,
    nameSuffix = "copy",
  } = options;

  const groupObjects = getObjectsByGroupId(objects, groupId).sort((a, b) => {
    const az = a.z_index ?? 0;
    const bz = b.z_index ?? 0;
    if (az !== bz) return az - bz;
    return a.id > b.id ? 1 : -1;
  });

  if (!groupObjects.length) return [];

  const maxZ = objects.length
    ? Math.max(...objects.map((obj) => obj.z_index ?? 0))
    : 0;

  return groupObjects.map((obj, index) => ({
    ...deepClone(obj),
    id: `temp-group-${Date.now()}-${index}-${Math.floor(Math.random() * 10000)}`,
    name: `${obj.name || obj.type || "object"}-${nameSuffix}-${Date.now()}-${index + 1}`,
    x: Number(obj.x || 0) + offsetX,
    y: Number(obj.y || 0) + offsetY,
    z_index: maxZ + index + 1,
    group: newGroupId,
    isNew: true,
    style_json: deepClone(obj.style_json || {}),
    data_json: deepClone(obj.data_json || {}),
    meta_json: deepClone(obj.meta_json || {}),
  }));
};

export const deleteGroupObjects = (objects = [], groupId = null) => {
  if (!groupId) return objects;
  return objects.filter((obj) => obj.group !== groupId);
};

export const getGroupRepresentative = (objects = [], groupId = null) => {
  const groupObjects = getObjectsByGroupId(objects, groupId);

  if (!groupObjects.length) return null;

  return [...groupObjects].sort((a, b) => {
    const az = a.z_index ?? 0;
    const bz = b.z_index ?? 0;
    if (az !== bz) return az - bz;
    return a.id > b.id ? 1 : -1;
  })[0];
};

export const resizeGroupObjects = (
  objects = [],
  groupId = null,
  startBounds,
  nextBounds
) => {
  if (!groupId || !startBounds || !nextBounds) return objects;

  const safeStartWidth = Math.max(1, startBounds.width);
  const safeStartHeight = Math.max(1, startBounds.height);

  const scaleX = nextBounds.width / safeStartWidth;
  const scaleY = nextBounds.height / safeStartHeight;

  return objects.map((obj) => {
    if (obj.group !== groupId) return obj;

    const relativeX = obj.x - startBounds.x;
    const relativeY = obj.y - startBounds.y;
    const relativeRight = relativeX + obj.width;
    const relativeBottom = relativeY + obj.height;

    const nextX = nextBounds.x + relativeX * scaleX;
    const nextY = nextBounds.y + relativeY * scaleY;
    const nextRight = nextBounds.x + relativeRight * scaleX;
    const nextBottom = nextBounds.y + relativeBottom * scaleY;

    return {
      ...obj,
      x: Math.round(nextX),
      y: Math.round(nextY),
      width: Math.max(1, Math.round(nextRight - nextX)),
      height: Math.max(1, Math.round(nextBottom - nextY)),
    };
  });
};